import type { Cell, Game, GameEvent, GameInput } from './types'

const W = 40
const H = 24
const COLS = 5
const ROWS = 4
const COL_STEP = 7
const ROW_STEP = 4
const SPRITES = [
	['.#.#.', '#####', '#.#.#'],
	['.#.#.', '#####', '.#.#.'],
]
const BOOM = ['#.#.#', '.###.', '#.#.#']
const SHIP = ['..#..', '#####']
const ROW_COLOURS = [0xff2bd6, 0xffd84a, 0x22f0ff, 0x7cff5a]
const PLAYER_Y = H - 2

interface Bullet { x: number; y: number }
interface Boom { x: number; y: number; ttl: number }

/** Space Invaders on a 40x24 board. 20 invaders, one player bullet at a time, three lives. */
export class Invaders implements Game {
	readonly name = 'invaders'
	readonly W = W
	readonly H = H
	readonly tickMs = 50
	score = 0
	over = false
	lives = 3
	wave = 1
	private alive: boolean[] = []
	private ox = 2
	private oy = 2
	private dx = 1
	private frame = 0
	private tick = 0
	private px = Math.floor(W / 2) - 2
	private shot: Bullet | null = null
	private bombs: Bullet[] = []
	private booms: Boom[] = []
	private respawn = 0
	private aiMoveTick = 0

	constructor() {
		this.reset()
	}

	private reset(): void {
		this.alive = Array(COLS * ROWS).fill(true)
		this.ox = 2
		this.oy = 1
		this.dx = 1
		this.shot = null
		this.bombs = []
	}

	private get aliveCount(): number {
		return this.alive.filter(Boolean).length
	}

	private moveEvery(): number {
		return Math.max(2, Math.round((2 + this.aliveCount * 0.6) / Math.sqrt(this.wave)))
	}

	step(input: GameInput, demo: boolean): GameEvent[] {
		const events: GameEvent[] = []
		this.tick++
		if (this.respawn > 0) {
			this.respawn--
			return events
		}

		// player
		const want = demo ? this.ai() : input
		if (want.held === 'left') this.px = Math.max(0, this.px - 1)
		if (want.held === 'right') this.px = Math.min(W - 5, this.px + 1)
		if (want.fire && !this.shot) {
			this.shot = { x: this.px + 2, y: PLAYER_Y - 1 }
			events.push('shoot')
		}

		// player bullet
		if (this.shot) {
			this.shot.y--
			if (this.shot.y < 0) this.shot = null
			else {
				const hit = this.invaderAt(this.shot.x, this.shot.y)
				if (hit >= 0) {
					this.alive[hit] = false
					const row = Math.floor(hit / COLS)
					this.score += (ROWS - row) * 10
					const [ix, iy] = this.invaderPos(hit)
					this.booms.push({ x: ix, y: iy, ttl: 4 })
					this.shot = null
					events.push('hit')
				} else {
					const b = this.bombs.findIndex((bb) => bb.x === this.shot!.x && Math.abs(bb.y - this.shot!.y) <= 1)
					if (b >= 0) {
						this.bombs.splice(b, 1)
						this.shot = null
					}
				}
			}
		}

		// formation
		if (this.tick % this.moveEvery() === 0 && this.aliveCount > 0) {
			const [minX, maxX] = this.formationBounds()
			if ((this.dx > 0 && maxX + 1 >= W) || (this.dx < 0 && minX - 1 < 0)) {
				this.dx = -this.dx
				this.oy++
			} else this.ox += this.dx
			this.frame ^= 1
			// bottom invader of a random column drops a bomb
			if (Math.random() < Math.min(0.9, 0.25 + this.wave * 0.1)) {
				const col = Math.floor(Math.random() * COLS)
				for (let row = ROWS - 1; row >= 0; row--) {
					const i = row * COLS + col
					if (this.alive[i]) {
						const [ix, iy] = this.invaderPos(i)
						this.bombs.push({ x: ix + 2, y: iy + 3 })
						break
					}
				}
			}
		}

		// bombs
		if (this.tick % 2 === 0) {
			for (const b of this.bombs) b.y++
			this.bombs = this.bombs.filter((b) => b.y < H)
			const hitMe = this.bombs.find((b) => b.y >= PLAYER_Y && b.y <= PLAYER_Y + 1 && b.x >= this.px && b.x < this.px + 5)
			if (hitMe) {
				this.lives--
				this.bombs = []
				this.shot = null
				this.booms.push({ x: this.px, y: PLAYER_Y, ttl: 8 })
				events.push('lose-life')
				if (this.lives <= 0) this.over = true
				else this.respawn = 16
			}
		}

		for (const b of this.booms) b.ttl--
		this.booms = this.booms.filter((b) => b.ttl > 0)

		// invaders landed?
		const lowest = this.formationBottom()
		if (lowest >= PLAYER_Y - 1) {
			this.over = true
			events.push('die')
		}

		// wave cleared
		if (this.aliveCount === 0) {
			this.wave++
			this.score += 50
			this.reset()
			events.push('wave')
		}
		return events
	}

	cells(): Cell[] {
		const out: Cell[] = []
		const sprite = SPRITES[this.frame]
		for (let i = 0; i < COLS * ROWS; i++) {
			if (!this.alive[i]) continue
			const [ix, iy] = this.invaderPos(i)
			const colour = ROW_COLOURS[Math.floor(i / COLS)]
			this.blit(out, sprite, ix, iy, colour)
		}
		for (const b of this.booms) this.blit(out, BOOM, b.x, b.y, 0xffffff)
		if (this.respawn === 0 || this.tick % 4 < 2) this.blit(out, SHIP, this.px, PLAYER_Y, 0xffd84a)
		if (this.shot) out.push({ x: this.shot.x, y: this.shot.y, color: 0xffffff })
		for (const b of this.bombs) out.push({ x: b.x, y: b.y, color: 0xff3b6b })
		// ground line
		for (let x = 0; x < W; x += 2) out.push({ x, y: H - 1, color: 0x22f0ff })
		return out
	}

	// ---- internals ----

	private invaderPos(i: number): [number, number] {
		const col = i % COLS
		const row = Math.floor(i / COLS)
		return [this.ox + col * COL_STEP, this.oy + row * ROW_STEP]
	}

	private invaderAt(x: number, y: number): number {
		for (let i = 0; i < COLS * ROWS; i++) {
			if (!this.alive[i]) continue
			const [ix, iy] = this.invaderPos(i)
			if (x >= ix && x < ix + 5 && y >= iy && y < iy + 3) return i
		}
		return -1
	}

	private formationBounds(): [number, number] {
		let min = W
		let max = -1
		for (let i = 0; i < COLS * ROWS; i++) {
			if (!this.alive[i]) continue
			const [ix] = this.invaderPos(i)
			min = Math.min(min, ix)
			max = Math.max(max, ix + 4)
		}
		return [min, max]
	}

	private formationBottom(): number {
		let bottom = -1
		for (let i = 0; i < COLS * ROWS; i++) {
			if (!this.alive[i]) continue
			bottom = Math.max(bottom, this.invaderPos(i)[1] + 2)
		}
		return bottom
	}

	private blit(out: Cell[], sprite: string[], x: number, y: number, color: number): void {
		for (let r = 0; r < sprite.length; r++)
			for (let c = 0; c < sprite[r].length; c++) if (sprite[r][c] === '#') out.push({ x: x + c, y: y + r, color })
	}

	/** Demo pilot: dodge bombs, line up under the nearest bottom invader, shoot. */
	private ai(): GameInput {
		const centre = this.px + 2
		const threat = this.bombs.find((b) => b.y > H - 11 && Math.abs(b.x - centre) <= 3)
		if (threat) {
			const goLeft = threat.x >= centre ? this.px > 0 : this.px >= W - 5
			return { held: goLeft ? 'left' : 'right', fire: Math.random() < 0.3 }
		}
		let target = -1
		let best = Infinity
		for (let col = 0; col < COLS; col++) {
			for (let row = ROWS - 1; row >= 0; row--) {
				const i = row * COLS + col
				if (!this.alive[i]) continue
				const [ix] = this.invaderPos(i)
				const d = Math.abs(ix + 2 - centre) + (ROWS - row) * 2
				if (d < best) { best = d; target = ix + 2 }
				break
			}
		}
		if (target < 0) return {}
		this.aiMoveTick++
		const move = this.aiMoveTick % 2 === 0
		const held = !move ? undefined : target > centre + 1 ? 'right' : target < centre - 1 ? 'left' : undefined
		return { held, fire: Math.abs(target - centre) <= 1 && Math.random() < 0.8 }
	}
}
